/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.0.0
 */

import React, { Fragment, useEffect } from 'react';

import { connect, useSelector } from 'react-redux';
import type { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { tableListUrlSelector } from '@/main/modules/table/selectors';
import type { IRootReducer } from '@/main/reducers/rootReducer';
import { icons } from '@assets/index';
import EllipseText from '@components/common/EllipsisText/EllipsisText';
import { BlockWithIcon, LinkBlock } from '@components/common/index';
import { KeyboardKeyCodeEnum } from '@modules/app/appConstants';
import type { IPageSubscriberMobileTranslation } from '@modules/internationalization/types/index';
import { modalOpen } from '@modules/modal/modalActions';
import type { IModalOpenPayload } from '@modules/modal/types/modalActionTypes';
import type { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { useTableListApi } from '@modules/table';

import { clearFilter, loadExtFilter, setExtFilterTabIndex } from '../actions/extFilterApiActions';
import { makeSelectExtFilter, makeSelectIsClearFilterLoading } from '../extFilterSelectors';
import type { IExtFilterItem, ISubscriberMobileExtFilter } from '../types/extFilterTypes';
import ExtFilterPopup from './ExtFilterPopup/ExtFilterPopup';
import {
    ClearExtFilterStyled,
    ExtFilterValueAndStyled,
    ExtFilterValueContainerStyled,
    ExtFilterValueItemListStyled,
    ExtFilterValueItemStyled,
} from './styled/SubscriberMobileExtFilterValueStyled';

/**
 * @description Connected props
 * @interface IConnectedProps
 * @property {ISubscriberMobileExtFilter} extFilter - external filter data
 */
interface IConnectedProps {
    extFilter: ISubscriberMobileExtFilter;
}

/**
 * @description Dispatch props
 * @interface IDispatchProps
 * @property {Function} loadExtFilterSaga - load external filter data
 * @property {Function} clearFilterSaga - clear external filter data
 */
interface IDispatchProps {
    clearFilter: () => void;
    loadExtFilter: () => void;
    openModal: (p: IModalOpenPayload) => void;
    setExtFilterTabIndex: (payload?: number) => void;
}

/**
 * @description Ext filter value props
 * @interface IExtFilterValueProps
 * @property {IPageSubscriberMobileTranslation} translation - locale
 */
interface IExtFilterValueProps {
    translation: IPageSubscriberMobileTranslation;
}

export type ExtFilterValue = IExtFilterValueProps & IDispatchProps & IConnectedProps;

export const SubscriberMobileExtFilterValue = (props: ExtFilterValue) => {

    const { initialLoad: loadTableList } = useTableListApi();

    const isClearFilterLoading = useSelector(makeSelectIsClearFilterLoading());
    const listUrl = useSelector(tableListUrlSelector);
    
    const items = props.extFilter.items || [];
    const showValue = items.length > 0;

    const onFilterItemClick = (index: number) => {
        props.openModal({
            render: (p: IModalRenderProps) => <ExtFilterPopup
                closeExtFilter={() => closeExtFilter(p.onCloseModal)}
                translation={props.translation.extFilter}
            />,
        });
        props.setExtFilterTabIndex(index);
    };

    const closeExtFilter = (closeModal: () => void) => {
        closeModal();
        props.setExtFilterTabIndex();
    };

    const onKeyHandler = (event: React.KeyboardEvent, index: number) => {
        if (event.keyCode === KeyboardKeyCodeEnum.ENTER) {
            onFilterItemClick(index);
        }
    };

    useEffect(() => {
        if (!isClearFilterLoading && !showValue && listUrl) {
            loadTableList();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[isClearFilterLoading, listUrl, showValue]);
    
    useEffect(() => {
        props.loadExtFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <ExtFilterValueContainerStyled showValue={showValue}>
            <ExtFilterValueItemListStyled>
                {
                    items.map((item: IExtFilterItem, index: number) =>
                        <Fragment key={index}>
                            {
                                item.operation &&
                                <ExtFilterValueAndStyled>
                                    {props.translation.extFilter[item.operation]}
                                </ExtFilterValueAndStyled>
                            }
                            <ExtFilterValueItemStyled
                                onClick={() => onFilterItemClick(index)}
                                onKeyDown={(e: React.KeyboardEvent) => onKeyHandler(e, index)}
                            >
                                <EllipseText title={item.valueName}>
                                    {
                                        item.attr &&
                                        props.translation.extFilter[item.attr]
                                    }
                                    &nbsp;
                                    {
                                        item.operator &&
                                        props.translation.extFilter[item.operator]
                                    }
                                    &nbsp;
                                    {
                                        item.valueName
                                    }
                                </EllipseText>
                            </ExtFilterValueItemStyled>
                        </Fragment>,
                    )
                }
            </ExtFilterValueItemListStyled>
            <ClearExtFilterStyled
                onClick={() => props.clearFilter()}
            >
                <BlockWithIcon
                    className="page-title__block-with-icon block-with-icon__update"
                    iconLeft={icons.cancelMiddleIcon}
                    iconLeftSize={24}
                >
                    <LinkBlock>
                        {props.translation.title.dropFilters}
                    </LinkBlock>
                </BlockWithIcon>
            </ClearExtFilterStyled>
        </ExtFilterValueContainerStyled>
    );
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    extFilter: makeSelectExtFilter(),
});

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    clearFilter: () => dispatch(clearFilter()),
    loadExtFilter: () => dispatch(loadExtFilter()),
    openModal: (payload: IModalOpenPayload) => dispatch(modalOpen(payload)),
    setExtFilterTabIndex: (payload?: number) => dispatch(setExtFilterTabIndex(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SubscriberMobileExtFilterValue);
